import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const flat = (color: string, extra: Partial<THREE.MeshStandardMaterialParameters> = {}) =>
  new THREE.MeshStandardMaterial({ color, roughness: 0.85, metalness: 0, flatShading: true, ...extra });

const wood = flat("#8a5a3b");
const woodDark = flat("#5b3a26");
const lacquer = flat("#b3261e");
const paperRed = flat("#d62828", { emissive: "#5c0f0f", roughness: 0.7 });
const lanternCap = flat("#1d1d1b");
const rope = flat("#3a2a1e");
const indigo = flat("#22305c");
const white = flat("#f4f2ee");
const offWhite = flat("#e4e1da");
const violet = flat("#6d3fc0");
const firstAidRed = flat("#d62828");
const metal = flat("#9aa0a6", { metalness: 0.4, roughness: 0.5 });

function merged(geometries: THREE.BufferGeometry[], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((geometry) => geometry.toNonIndexed())), material);
  mesh.name = name;
  return mesh;
}

function box(width: number, height: number, depth: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  geometry.translate(x, y, z);
  return geometry;
}

function cylinder(radius: number, height: number, x: number, y: number, z: number, segments = 8): THREE.BufferGeometry {
  const geometry = new THREE.CylinderGeometry(radius, radius, height, segments);
  geometry.translate(x, y, z);
  return geometry;
}

/** Croix de secours ou « i » d'information, dans le plan xy, épaisse de `depth`. */
function crossGlyph(size: number, depth: number, x: number, y: number, z: number): THREE.BufferGeometry[] {
  const bar = size / 3;
  return [box(size, bar, depth, x, y, z), box(bar, size, depth, x, y, z)];
}

function infoGlyph(size: number, depth: number, x: number, y: number, z: number): THREE.BufferGeometry[] {
  const dot = new THREE.CylinderGeometry(size * 0.13, size * 0.13, depth, 12);
  dot.rotateX(Math.PI / 2);
  dot.translate(x, y + size * 0.32, z);
  return [dot, box(size * 0.2, size * 0.5, depth, x, y - size * 0.13, z), box(size * 0.34, size * 0.08, depth, x, y - size * 0.34, z)];
}

/** Lanterne de papier : un corps bombé entre deux bagues noires, centré sur `y`. */
function lantern(x: number, y: number, z: number, radius: number): { paper: THREE.BufferGeometry; caps: THREE.BufferGeometry[] } {
  const paper = new THREE.SphereGeometry(radius, 10, 8);
  paper.scale(1, 1.3, 1);
  paper.translate(x, y, z);
  const capHeight = radius * 0.25;
  return {
    paper,
    caps: [
      cylinder(radius * 0.6, capHeight, x, y + radius * 1.25, z),
      cylinder(radius * 0.6, capHeight, x, y - radius * 1.25, z),
    ],
  };
}

/** Guirlande de chōchin rouges, tendue entre deux poteaux de bois sur environ 6 m. */
export function buildLanternGarland(): THREE.Group {
  const root = new THREE.Group();
  const span = 6;
  const postHeight = 3.2;
  const sag = 0.45;
  const half = span / 2;

  root.add(merged([
    cylinder(0.1, postHeight, -half, postHeight / 2, 0),
    cylinder(0.1, postHeight, half, postHeight / 2, 0),
    box(0.3, 0.06, 0.3, -half, 0.03, 0),
    box(0.3, 0.06, 0.3, half, 0.03, 0),
  ], woodDark, "Posts"));

  const ropeAt = (t: number) => new THREE.Vector3(-half + span * t, postHeight - 0.1 - sag * 4 * t * (1 - t), 0);
  const curve = new THREE.CatmullRomCurve3(Array.from({ length: 13 }, (_, i) => ropeAt(i / 12)));
  root.add(merged([new THREE.TubeGeometry(curve, 24, 0.02, 4, false)], rope, "Rope"));

  const papers: THREE.BufferGeometry[] = [];
  const caps: THREE.BufferGeometry[] = [];
  const count = 7;
  for (let i = 0; i < count; i++) {
    const anchor = ropeAt((i + 1) / (count + 1));
    const radius = 0.26;
    caps.push(cylinder(0.008, 0.12, anchor.x, anchor.y - 0.06, 0, 4));
    const piece = lantern(anchor.x, anchor.y - 0.12 - radius * 1.4, 0, radius);
    papers.push(piece.paper);
    caps.push(...piece.caps);
  }
  root.add(merged(papers, paperRed, "Lanterns"));
  root.add(merged(caps, lanternCap, "LanternCaps"));
  return root;
}

/** Yatai : échoppe de rue sur roues, comptoir de bois, toit de laque rouge, noren et lanternes. */
export function buildYatai(): THREE.Group {
  const root = new THREE.Group();
  const width = 2.4;
  const depth = 1.3;
  const counterHeight = 1;
  const roofHeight = 2.35;
  const halfW = width / 2;
  const halfD = depth / 2;

  root.add(merged([
    box(width, counterHeight - 0.25, depth, 0, 0.25 + (counterHeight - 0.25) / 2, 0),
    box(width + 0.2, 0.06, depth + 0.35, 0, counterHeight + 0.03, 0.12),
    ...[-1, 1].flatMap((sx) => [-1, 1].map((sz) => box(0.08, roofHeight - counterHeight, 0.08, sx * (halfW - 0.04), (roofHeight + counterHeight) / 2, sz * (halfD - 0.04)))),
    box(width + 0.1, 0.1, 0.1, 0, roofHeight - 0.05, halfD - 0.04),
    box(width + 0.1, 0.1, 0.1, 0, roofHeight - 0.05, -halfD + 0.04),
  ], wood, "Frame"));

  const wheels = [-1, 1].map((sx) => {
    const wheel = new THREE.CylinderGeometry(0.38, 0.38, 0.08, 14);
    wheel.rotateZ(Math.PI / 2);
    wheel.translate(sx * (halfW + 0.06), 0.38, -0.1);
    return wheel;
  });
  root.add(merged([...wheels, box(width + 0.3, 0.05, 0.05, 0, 0.38, -0.1)], woodDark, "Wheels"));

  const slope = 0.42;
  const roofDepth = depth + 0.9;
  const panel = (side: number) => {
    const geometry = new THREE.BoxGeometry(width + 0.5, 0.06, roofDepth / 2 / Math.cos(slope));
    geometry.translate(0, 0, (side * roofDepth) / 4 / Math.cos(slope));
    geometry.rotateX(side * slope);
    geometry.translate(0, roofHeight + Math.tan(slope) * roofDepth / 2, 0);
    return geometry;
  };
  root.add(merged([panel(1), panel(-1)], lacquer, "Roof"));

  const stripCount = 4;
  const stripWidth = (width - 0.2) / stripCount - 0.04;
  root.add(merged(
    Array.from({ length: stripCount }, (_, i) => box(stripWidth, 0.45, 0.02, -halfW + 0.1 + (i + 0.5) * (stripWidth + 0.04), roofHeight - 0.32, halfD + 0.02)),
    indigo,
    "Noren",
  ));

  const papers: THREE.BufferGeometry[] = [];
  const caps: THREE.BufferGeometry[] = [];
  for (const sx of [-1, 1]) {
    const piece = lantern(sx * (halfW + 0.05), roofHeight - 0.5, halfD + 0.25, 0.16);
    papers.push(piece.paper);
    caps.push(...piece.caps, cylinder(0.008, 0.2, sx * (halfW + 0.05), roofHeight - 0.18, halfD + 0.25, 4));
  }
  root.add(merged(papers, paperRed, "Lanterns"));
  root.add(merged(caps, lanternCap, "LanternCaps"));

  root.add(merged(
    [-0.7, 0, 0.7].flatMap((x) => [cylinder(0.17, 0.05, x, 0.62, halfD + 0.75, 10), cylinder(0.03, 0.6, x, 0.3, halfD + 0.75, 5)]),
    woodDark,
    "Stools",
  ));
  return root;
}

/** Comptoir d'accueil : banque d'information et panneau « i » visible des deux côtés. */
export function buildInfoDesk(): THREE.Group {
  const root = new THREE.Group();
  const width = 3;
  const depth = 0.8;
  const height = 1.05;

  root.add(merged([
    box(width, height - 0.05, depth, 0, (height - 0.05) / 2, 0),
    box(0.8, height - 0.05, 1.6, -width / 2 + 0.4, (height - 0.05) / 2, -0.4),
  ], violet, "Desk"));
  root.add(merged([
    box(width + 0.1, 0.05, depth + 0.1, 0, height - 0.025, 0),
    box(0.9, 0.05, 1.7, -width / 2 + 0.4, height - 0.025, -0.4),
    box(width - 0.3, 0.04, 0.02, 0, height - 0.2, depth / 2 + 0.01),
  ], white, "Top"));

  const signY = 2.7;
  const signSize = 0.9;
  root.add(merged([
    cylinder(0.05, signY - signSize / 2, width / 2 - 0.3, (signY - signSize / 2) / 2, -depth / 2 - 0.2),
  ], metal, "Pole"));
  root.add(merged([box(signSize, signSize, 0.06, width / 2 - 0.3, signY, -depth / 2 - 0.2)], violet, "Sign"));
  root.add(merged([
    ...infoGlyph(signSize * 0.75, 0.02, width / 2 - 0.3, signY, -depth / 2 - 0.2 + 0.04),
    ...infoGlyph(signSize * 0.75, 0.02, width / 2 - 0.3, signY, -depth / 2 - 0.2 - 0.04),
    ...infoGlyph(0.6, 0.02, -0.4, height * 0.5, depth / 2 + 0.01),
  ], white, "Glyph"));

  root.add(merged([
    box(0.5, 0.32, 0.03, 0.5, height + 0.2, -0.15),
    box(0.06, 0.18, 0.06, 0.5, height + 0.04, -0.15),
  ], lanternCap, "Screen"));
  root.add(merged(
    [-0.3, 0.8].flatMap((x) => [cylinder(0.2, 0.06, x, 0.72, -0.8, 12), cylinder(0.03, 0.7, x, 0.35, -0.8, 5)]),
    metal,
    "Stools",
  ));
  return root;
}

/** Poste de secours : tente blanche ouverte à l'avant, croix rouges, brancard et armoire. */
export function buildFirstAidPost(): THREE.Group {
  const root = new THREE.Group();
  const size = 3;
  const half = size / 2;
  const wall = 2.1;

  root.add(merged(
    [-1, 1].flatMap((sx) => [-1, 1].map((sz) => cylinder(0.04, wall, sx * (half - 0.04), wall / 2, sz * (half - 0.04), 6))),
    metal,
    "Poles",
  ));
  root.add(merged([
    box(size, wall, 0.04, 0, wall / 2, -half),
    box(0.04, wall, size, -half, wall / 2, 0),
    box(0.04, wall, size, half, wall / 2, 0),
    box(size, 0.3, 0.04, 0, wall - 0.15, half),
  ], white, "Walls"));

  const roof = new THREE.ConeGeometry(half * Math.SQRT2 + 0.1, 1, 4);
  roof.rotateY(Math.PI / 4);
  roof.translate(0, wall + 0.5, 0);
  root.add(merged([roof], offWhite, "Roof"));

  root.add(merged([
    ...crossGlyph(0.24, 0.02, 0, wall - 0.15, half + 0.03),
    ...crossGlyph(0.9, 0.02, 0, 1.2, -half + 0.03),
    ...crossGlyph(0.9, 0.02, 0, 1.2, -half - 0.03),
  ], firstAidRed, "Cross"));
  root.add(merged([...crossGlyph(0.8, 0.02, 0, 1.2, 0)].map((geometry) => {
    geometry.rotateY(Math.PI / 2);
    geometry.translate(half + 0.03, 0, 0);
    return geometry;
  }), firstAidRed, "CrossSide"));

  root.add(merged([
    box(0.7, 0.08, 1.9, -0.6, 0.6, -0.2),
    ...[-1, 1].flatMap((sx) => [-1, 1].map((sz) => cylinder(0.02, 0.56, -0.6 + sx * 0.3, 0.28, -0.2 + sz * 0.85, 5))),
  ], metal, "StretcherFrame"));
  root.add(merged([box(0.66, 0.06, 1.8, -0.6, 0.67, -0.2)], firstAidRed, "Stretcher"));
  root.add(merged([box(0.6, 1.4, 0.45, 0.95, 0.7, -half + 0.3)], white, "Cabinet"));
  return root;
}
